import { useEffect, useRef } from "react";
import { countryToCurrency } from "./countryCurrencyMap";
import { useCurrency } from "./CurrencyContext";

const LOCAL_STORAGE_KEY = "tvz_currency";
const FORCE_CURRENCY_KEY = "tvz_force_currency";
const LEGACY_CODE_KEY = "tvz_currency_code";
const LEGACY_SYMBOL_KEY = "tvz_currency_symbol";

const OLD_LOCAL_STORAGE_KEY = "ticketverz_currency";
const OLD_FORCE_CURRENCY_KEY = "ticketverz_force_currency";
const OLD_LEGACY_CODE_KEY = "currency_code";
const OLD_LEGACY_SYMBOL_KEY = "currency_symbol";

const DEFAULT_CURRENCY = { code: "EUR", symbol: "€" };

const IP_API_URL = "https://ip-api.com/json/?fields=status,countryCode";
const NOMINATIM_URL =
  "https://nominatim.openstreetmap.org/reverse?format=json&zoom=3";

const GEO_TIMEOUT_MS = 4000;
const FETCH_TIMEOUT_MS = 5000;

function fetchWithTimeout(url, ms = FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);

  return fetch(url, { signal: controller.signal }).finally(() =>
    clearTimeout(timer),
  );
}

async function detectByIP() {
  try {
    const res = await fetchWithTimeout(IP_API_URL, 4000);
    if (!res.ok) return null;

    const data = await res.json();

    if (data?.status === "success" && data?.countryCode) {
      return data.countryCode.toUpperCase();
    }

    return null;
  } catch {
    return null;
  }
}

async function reverseGeocode(lat, lng) {
  try {
    const url = `${NOMINATIM_URL}&lat=${lat}&lon=${lng}`;
    const res = await fetchWithTimeout(url, 5000);
    if (!res.ok) return null;

    const data = await res.json();
    return data?.address?.country_code?.toUpperCase() || null;
  } catch {
    return null;
  }
}

function getUserLocation() {
  return new Promise((resolve) => {
    if (!("geolocation" in navigator)) {
      resolve(null);
      return;
    }

    const timer = setTimeout(() => resolve(null), GEO_TIMEOUT_MS);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        resolve(pos.coords);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
      {
        maximumAge: 600000,
        timeout: GEO_TIMEOUT_MS,
        enableHighAccuracy: false,
      },
    );
  });
}

function detectCountryFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const timezoneCountryMap = {
      "Europe/London": "GB",
      "Europe/Berlin": "DE",
      "Europe/Paris": "FR",
      "Europe/Amsterdam": "NL",
      "Europe/Brussels": "BE",
      "Europe/Vienna": "AT",
      "Europe/Zurich": "CH",
      "Europe/Rome": "IT",
      "Europe/Madrid": "ES",
      "Europe/Lisbon": "PT",
      "Europe/Warsaw": "PL",
      "Europe/Stockholm": "SE",
      "Europe/Copenhagen": "DK",
      "Europe/Oslo": "NO",
      "Europe/Helsinki": "FI",
      "Europe/Dublin": "IE",
      "Europe/Prague": "CZ",
      "Europe/Budapest": "HU",
      "Europe/Bucharest": "RO",
      "Europe/Sofia": "BG",
      "Europe/Athens": "GR",
      "Europe/Istanbul": "TR",
      "Europe/Tirane": "AL",
      "Europe/Skopje": "MK",
      "Europe/Belgrade": "RS",
      "Europe/Sarajevo": "BA",
      "Europe/Zagreb": "HR",
      "Europe/Vilnius": "LT",
      "Europe/Riga": "LV",
      "Europe/Tallinn": "EE",
      "Europe/Kyiv": "UA",
      "Europe/Reykjavik": "IS",
      "Europe/Minsk": "BY",
      "Europe/Tbilisi": "GE",
      "Europe/Yerevan": "AM",
      "Europe/Baku": "AZ",
      "Asia/Kolkata": "IN",
      "Asia/Calcutta": "IN",
      "America/New_York": "US",
      "America/Chicago": "US",
      "America/Denver": "US",
      "America/Los_Angeles": "US",
      "America/Toronto": "CA",
      "Australia/Sydney": "AU",
    };

    return timezoneCountryMap[tz] || null;
  } catch {
    return null;
  }
}

function detectCountryFromLocale() {
  try {
    const locale = navigator.language || "";
    const region = locale.split("-")[1];
    return region ? region.toUpperCase() : null;
  } catch {
    return null;
  }
}

export default function LocationCurrencyDetector() {
  const { currency, setCurrencyDetected, setIsDetecting } = useCurrency();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    try {
      const forced =
        localStorage.getItem(FORCE_CURRENCY_KEY) ||
        localStorage.getItem(OLD_FORCE_CURRENCY_KEY);

      const cached =
        localStorage.getItem(LOCAL_STORAGE_KEY) ||
        localStorage.getItem(OLD_LOCAL_STORAGE_KEY);

      const legacyCode =
        localStorage.getItem(LEGACY_CODE_KEY) ||
        localStorage.getItem(OLD_LEGACY_CODE_KEY);

      const legacySymbol =
        localStorage.getItem(LEGACY_SYMBOL_KEY) ||
        localStorage.getItem(OLD_LEGACY_SYMBOL_KEY);

      if (forced) {
        setIsDetecting(false);
        return;
      }

      if (cached || (legacyCode && legacySymbol)) {
        setIsDetecting(false);
        return;
      }
    } catch {}

    async function detect() {
      let countryCode = null;

      const coords = await getUserLocation();

      if (coords) {
        countryCode = await reverseGeocode(coords.latitude, coords.longitude);
      }

      if (!countryCode) {
        countryCode = detectCountryFromTimezone();
      }

      if (!countryCode) {
        countryCode = detectCountryFromLocale();
      }

      if (!countryCode) {
        countryCode = await detectByIP();
      }

      const next = countryCode
        ? countryToCurrency(countryCode)
        : DEFAULT_CURRENCY;

      const currentCode = currency?.code || DEFAULT_CURRENCY.code;

      setCurrencyDetected(next);

      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
        localStorage.setItem(LEGACY_CODE_KEY, next.code);
        localStorage.setItem(LEGACY_SYMBOL_KEY, next.symbol);
      } catch {}

      if (next.code !== currentCode) {
        window.location.reload();
      }
    }

    detect()
      .catch(() => {
        setCurrencyDetected(DEFAULT_CURRENCY);
      })
      .finally(() => {
        setIsDetecting(false);
      });
  }, [currency, setCurrencyDetected, setIsDetecting]);

  return null;
}
